import { CHARTV2_HORIZON_SPAN_MS } from '../constants';
import type { ChartV2Horizon, ChartV2Point } from '../types/chart-v2.types';

export const horizonToDomain = (
  horizon: ChartV2Horizon,
  latest: number,
  earliest: number
): { start: number; end: number } => {
  const end = latest;

  if (horizon === 'MAX') {
    return { start: earliest, end };
  }

  const span = CHARTV2_HORIZON_SPAN_MS[horizon];

  if (span == null) {
    return { start: earliest, end };
  }

  return { start: Math.max(end - span, earliest), end };
};

export const isOhlcPoint = (
  p: ChartV2Point
): p is Extract<ChartV2Point, { open: number }> => 'open' in p && 'close' in p;

export const pointValue = (p: ChartV2Point): number =>
  isOhlcPoint(p) ? p.close : p.value;

export const clamp = (x: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, x));

export const bisectLeft = (arr: number[], value: number): number => {
  let lo = 0;
  let hi = arr.length;

  while (lo < hi) {
    const mid = (lo + hi) >>> 1;

    if (arr[mid] < value) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  return lo;
};

/**
 * Continuous fractional bar index for a ms timestamp, linearly
 * interpolating between the two surrounding bars (or extrapolating past
 * either end using the nearest step). This is what lets pan/zoom on
 * index-based (bar-timeline) charts move by sub-bar amounts — mirroring
 * v1's `scaleLinear` index domain — instead of snapping to whole bars.
 */
export const indexAtTime = (tl: number[], ms: number): number => {
  const n = tl.length;

  if (n < 2) {
    return 0;
  }

  const idx = bisectLeft(tl, ms);

  if (idx <= 0) {
    const step = tl[1] - tl[0];
    return step === 0 ? 0 : (ms - tl[0]) / step;
  }

  if (idx >= n) {
    const step = tl[n - 1] - tl[n - 2];
    return step === 0 ? n - 1 : n - 1 + (ms - tl[n - 1]) / step;
  }

  if (tl[idx] === ms) {
    return idx;
  }

  const prev = tl[idx - 1];
  const next = tl[idx];
  const frac = next === prev ? 0 : (ms - prev) / (next - prev);

  return idx - 1 + frac;
};

/** Inverse of `indexAtTime` — interpolated ms value for a fractional bar index. */
export const timeAtIndex = (tl: number[], index: number): number => {
  const n = tl.length;

  if (n === 0) {
    return 0;
  }

  if (n === 1) {
    return tl[0];
  }

  if (index <= 0) {
    return tl[0] + index * (tl[1] - tl[0]);
  }

  if (index >= n - 1) {
    return tl[n - 1] + (index - (n - 1)) * (tl[n - 1] - tl[n - 2]);
  }

  const i0 = Math.floor(index);
  const frac = index - i0;

  return tl[i0] + frac * (tl[i0 + 1] - tl[i0]);
};

export const devicePixelRatioSafe = (): number =>
  typeof window !== 'undefined' && window.devicePixelRatio
    ? window.devicePixelRatio
    : 1;
